/**
 * 購入者用レイアウト
 *
 * @file BuyerLayout.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * 購入者向けの統合レイアウト（旧Publicロール統合済み）。
 * 認証前後で表示を切り替え、全ユーザーに対応。
 * ヘッダー、サブナビゲーション（認証後のみ）、フッターを提供。
 */

import React, { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import MockBanner from '@shared/components/MockBanner';
import { LanguageSelector } from '@shared/components/common';
import { useAuth } from '@shared/contexts/useAuth';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { useSideMenu } from '@shared/contexts/SideMenuContext';
import { ensureInitialized } from '@shared/i18n';
import { masterdataService } from '@/services/api/masterdata.service';
// import { getDashboardStatistics, DashboardStatisticsResponse } from '@/services/api/dashboard.service';
import type { Category } from '@/types';
import { isYauctionEnabled, isMercariEnabled, isAnyProxyEnabled } from '@/utils/proxyServiceConfig';

/**
 * URLパスから言語プレフィックスを除去し、正規化されたパスを返す
 * 例: /en/cart → /cart, /ko/products → /products, /cart → /cart
 */
const normalizePath = (path: string): string => {
  const langPrefixPattern = /^\/(ja|ko|en|zh)(\/|$)/;
  return path.replace(langPrefixPattern, '/');
};

const BuyerLayout: React.FC = () => {
  const { t, getLocalizedText } = useLanguage();
  const { sideMenuOpen, toggleSideMenu, closeSideMenu } = useSideMenu();
  const { isAuthenticated, logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showSideNav, setShowSideNav] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [parentCategories, setParentCategories] = useState<Category[]>([]);
  const [subCategoriesMap, setSubCategoriesMap] = useState<Record<string, Category[]>>({});
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  // const [dashboardStats, setDashboardStats] = useState<DashboardStatisticsResponse | null>(null);

  // URLパスから言語プレフィックスを除去した正規化パス
  const normalizedPath = normalizePath(location.pathname);

  // カートページまたはチェックアウトページかどうかを判定
  const isCartPage = normalizedPath === '/cart';
  const isCheckoutPage = normalizedPath === '/checkout';
  const isCheckoutResultPage = normalizedPath === '/checkout/result';

  // メルカリ・ヤフオクチェックアウトページの判定
  const isMercariCheckoutPage = /^\/mercari\/checkout\/[^/]+$/.test(normalizedPath);
  const isMercariCheckoutResultPage = /^\/mercari\/checkout\/[^/]+\/result$/.test(normalizedPath);
  const isYauctionCheckoutPage = /^\/yauction\/checkout\/[^/]+$/.test(normalizedPath);
  const isYauctionCheckoutResultPage = /^\/yauction\/checkout\/[^/]+\/result$/.test(normalizedPath);

  // 代理購入チェックアウト判定（いずれかに該当）
  const isProxyCheckoutPage = isMercariCheckoutPage || isYauctionCheckoutPage;
  const isProxyCheckoutResultPage = isMercariCheckoutResultPage || isYauctionCheckoutResultPage;

  // マイページかどうかを判定
  const isMyPage = normalizedPath.startsWith('/mypage');

  // i18nextの初期化
  useEffect(() => {
    ensureInitialized();
  }, []);

  // カテゴリー取得（階層対応：全件表示）
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        // 全カテゴリーを取得
        const allCategories = await masterdataService.getCategories({
          isActive: true
        });

        // 親カテゴリー（level 1）を全件取得
        const level1Parents = allCategories
          .filter(c => c.level === 1);
        setParentCategories(level1Parents);

        // 各親カテゴリーのサブカテゴリーを全件マッピング
        const subMap: Record<string, Category[]> = {};
        for (const parent of level1Parents) {
          const subs = allCategories
            .filter(c => c.parentId === parent.id);
          subMap[parent.id] = subs;
        }
        setSubCategoriesMap(subMap);
      } catch (error) {
        console.error('カテゴリー取得エラー:', error);
        throw error;
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // // ダッシュボード統計取得（認証後のみ）
  // useEffect(() => {
  //   const fetchDashboardStats = async () => {
  //     if (!isAuthenticated) return;

  //     try {
  //       const stats = await getDashboardStatistics();
  //       setDashboardStats(stats);
  //     } catch (error) {
  //       console.error('ダッシュボード統計の取得に失敗しました:', error);
  //       // エラー時はバッジを非表示（nullのまま）
  //     }
  //   };

  //   fetchDashboardStats();
  // }, [isAuthenticated]);

  // 検索実行処理
  const handleSearch = () => {
    if (searchKeyword.trim()) {
      const keyword = encodeURIComponent(searchKeyword.trim());

      // 現在のパスに応じて検索先を振り分け
      if (normalizedPath.startsWith('/mercari')) {
        navigate(`/mercari/products?keyword=${keyword}`);
      } else if (normalizedPath.startsWith('/yauction')) {
        navigate(`/yauction/products?keyword=${keyword}`);
      } else {
        navigate(`/products?keyword=${keyword}`);
      }
    }
  };


  // インタラクティブ機能の初期化
  useEffect(() => {
    // サイドナビゲーションの項目クリック時にタブ切り替え
    const handleNavClick = (e: Event) => {
      const link = e.currentTarget as HTMLElement;
      const href = link.getAttribute('href');
      const tabId = link.getAttribute('data-tab');

      // href属性を持つリンクでdata-tab属性がない場合はReact Routerに任せる
      if (href && !tabId) {
        // React Routerのナビゲーションを有効にする（preventDefault不要）
        return;
      }

      // data-tab属性を持つリンクの場合はタブ切り替え処理を実行
      if (tabId) {
        e.preventDefault(); // タブ切り替えのみpreventDefaultを実行

        // ナビメニューのアクティブ状態を切り替え
        document.querySelectorAll('.nav-menu-link').forEach(item => item.classList.remove('active'));
        link.classList.add('active');

        // タブコンテンツの表示/非表示を切り替え
        document.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));
        const targetTab = document.getElementById(tabId);
        if (targetTab) targetTab.classList.add('active');
      }
    };

    // モバイルナビゲーションのトグル
    const handleMobileNavToggle = () => {
      const navMenu = document.getElementById('nav-menu');
      if (navMenu) navMenu.classList.toggle('active');
    };

    // クーポンタブの切り替え
    const handleCouponTabClick = (e: Event) => {
      const tab = e.currentTarget as HTMLElement;
      // タブのアクティブ状態を切り替え
      document.querySelectorAll('[data-coupon-tab]').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // クーポンリストの表示を切り替え
      const tabId = tab.getAttribute('data-coupon-tab');
      const availableCoupons = document.getElementById('available-coupons');
      const usedCoupons = document.getElementById('used-coupons');
      const expiredCoupons = document.getElementById('expired-coupons');
      const targetCoupons = document.getElementById(`${tabId}-coupons`);
      
      if (availableCoupons) availableCoupons.style.display = 'none';
      if (usedCoupons) usedCoupons.style.display = 'none';
      if (expiredCoupons) expiredCoupons.style.display = 'none';
      if (targetCoupons) targetCoupons.style.display = 'grid';
    };

    // ダッシュボードの「すべて見る」リンク
    const handleSectionLinkClick = (e: Event) => {
      e.preventDefault();
      const link = e.currentTarget as HTMLElement;
      const tabId = link.getAttribute('data-tab');
      if (!tabId) return;

      // ナビメニューのアクティブ状態を切り替え
      document.querySelectorAll('.nav-menu-link').forEach(item => item.classList.remove('active'));
      const targetNavLink = document.querySelector(`.nav-menu-link[data-tab="${tabId}"]`);
      if (targetNavLink) targetNavLink.classList.add('active');

      // タブコンテンツの表示/非表示を切り替え
      document.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));
      const targetTab = document.getElementById(tabId);
      if (targetTab) targetTab.classList.add('active');
    };

    // ウィッシュリスト削除ボタン
    const handleWishlistRemove = (e: Event) => {
      const button = e.currentTarget as HTMLElement;
      const item = button.closest('.wishlist-item') as HTMLElement;
      if (item) {
        item.style.opacity = '0.5';
        item.style.pointerEvents = 'none';
        // 実際にはここでAPIリクエストを送信して削除処理を行う
        setTimeout(() => {
          item.remove();
        }, 500);
      }
    };

    // クーポンコードのコピー
    const handleCouponCopy = (e: Event) => {
      const button = e.currentTarget as HTMLElement;
      const codeElement = button.previousElementSibling;
      if (codeElement) {
        const couponCode = codeElement.textContent || '';
        navigator.clipboard.writeText(couponCode).then(() => {
          button.textContent = t('layout.buyer.actions.copied');
          setTimeout(() => {
            button.innerHTML = `<i class="material-icons">content_copy</i>${t('layout.buyer.actions.copy')}`;
          }, 2000);
        });
      }
    };

    // イベントリスナーの登録
    const navLinks = document.querySelectorAll('.nav-menu-link');
    const mobileNavToggle = document.getElementById('mobile-nav-toggle');
    const couponTabs = document.querySelectorAll('[data-coupon-tab]');
    const sectionLinks = document.querySelectorAll('.section-title-link');
    const wishlistRemoveButtons = document.querySelectorAll('.wishlist-item-remove');
    const couponCopyButtons = document.querySelectorAll('.coupon-code-copy');

    navLinks.forEach(link => link.addEventListener('click', handleNavClick));
    if (mobileNavToggle) mobileNavToggle.addEventListener('click', handleMobileNavToggle);
    couponTabs.forEach(tab => tab.addEventListener('click', handleCouponTabClick));
    sectionLinks.forEach(link => link.addEventListener('click', handleSectionLinkClick));
    wishlistRemoveButtons.forEach(button => button.addEventListener('click', handleWishlistRemove));
    couponCopyButtons.forEach(button => button.addEventListener('click', handleCouponCopy));

    // クリーンアップ
    return () => {
      navLinks.forEach(link => link.removeEventListener('click', handleNavClick));
      if (mobileNavToggle) mobileNavToggle.removeEventListener('click', handleMobileNavToggle);
      couponTabs.forEach(tab => tab.removeEventListener('click', handleCouponTabClick));
      sectionLinks.forEach(link => link.removeEventListener('click', handleSectionLinkClick));
      wishlistRemoveButtons.forEach(button => button.removeEventListener('click', handleWishlistRemove));
      couponCopyButtons.forEach(button => button.removeEventListener('click', handleCouponCopy));
    };
  }, [t]);

  // ページ遷移時にスクロールをトップに戻す
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  // URL変更時に検索キーワードを同期
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlKeyword = params.get('keyword') || '';
    setSearchKeyword(urlKeyword);
  }, [location.pathname, location.search]);

  const handleLogout = async () => {
    try {
      setShowSideNav(false);
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    // エラー発生時もログイン画面へ遷移
    navigate('/');
  };

  // ヘッダーのログインボタンクリックハンドラー
  const handleHeaderLoginClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    sessionStorage.setItem('redirectAfterLogin', location.pathname);
    navigate('/login', { state: { from: location } });
  };

  // サイドメニューのログインボタンクリックハンドラー
  const handleSideMenuLoginClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    closeSideMenu();
    sessionStorage.setItem('redirectAfterLogin', location.pathname);
    navigate('/login', { state: { from: location } });
  };

  // カート専用ヘッダーコンポーネント
  const CartHeader = () => (
    <>
      <div className="checkout-steps">
        <div className="step active">
          <div className="step-number">1</div>
          <div className="step-name">{t('layout.buyer.checkout_steps.cart')}</div>
          <div className="step-line"></div>
        </div>
        <div className="step">
          <div className="step-number">2</div>
          <div className="step-name">{t('layout.buyer.checkout_steps.checkout')}</div>
          <div className="step-line"></div>
        </div>
        <div className="step">
          <div className="step-number">3</div>
          <div className="step-name">{t('layout.buyer.checkout_steps.confirmation')}</div>
        </div>
      </div>
      <div className="nav-links">
        <Link to="/help" className="help-link">
          <i className="material-icons">help_outline</i>
          <span>{t('layout.buyer.header.help')}</span>
        </Link>
        <LanguageSelector />
      </div>
    </>
  );

  // チェックアウト専用ヘッダーコンポーネント
  const CheckoutHeader = () => (
    <>
      <div className="checkout-steps">
        <div className="step completed">
          <div className="step-number">1</div>
          <div className="step-name">{t('layout.buyer.checkout_steps.cart')}</div>
          <div className="step-line"></div>
        </div>
        <div className="step active">
          <div className="step-number">2</div>
          <div className="step-name">{t('layout.buyer.checkout_steps.checkout')}</div>
          <div className="step-line"></div>
        </div>
        <div className="step">
          <div className="step-number">3</div>
          <div className="step-name">{t('layout.buyer.checkout_steps.confirmation')}</div>
        </div>
      </div>
      <div className="nav-links">
        <Link to="/help" className="help-link">
          <i className="material-icons">help_outline</i>
          <span>{t('layout.buyer.header.help')}</span>
        </Link>
        <LanguageSelector />
      </div>
    </>
  );

  // チェックアウト結果専用ヘッダーコンポーネント
  const CheckoutResultHeader = () => (
    <>
      <div className="checkout-steps">
        <div className="step completed">
          <div className="step-number">1</div>
          <div className="step-name">{t('layout.buyer.checkout_steps.cart')}</div>
          <div className="step-line"></div>
        </div>
        <div className="step completed">
          <div className="step-number">2</div>
          <div className="step-name">{t('layout.buyer.checkout_steps.checkout')}</div>
          <div className="step-line"></div>
        </div>
        <div className="step active">
          <div className="step-number">3</div>
          <div className="step-name">{t('layout.buyer.checkout_steps.confirmation')}</div>
        </div>
      </div>
      <div className="nav-links">
        <Link to="/help" className="help-link">
          <i className="material-icons">help_outline</i>
          <span>{t('layout.buyer.header.help')}</span>
        </Link>
        <LanguageSelector />
      </div>
    </>
  );

  // 代理購入チェックアウト専用ヘッダー（2ステップ・ステップ1アクティブ）
  const ProxyCheckoutHeader = () => (
    <>
      <div className="checkout-steps">
        <div className="step active">
          <div className="step-number">1</div>
          <div className="step-name">{t('layout.buyer.proxy_checkout_steps.checkout')}</div>
          <div className="step-line"></div>
        </div>
        <div className="step">
          <div className="step-number">2</div>
          <div className="step-name">{t('layout.buyer.proxy_checkout_steps.confirmation')}</div>
        </div>
      </div>
      <div className="nav-links">
        <Link to="/help" className="help-link">
          <i className="material-icons">help_outline</i>
          <span>{t('layout.buyer.header.help')}</span>
        </Link>
        <LanguageSelector />
      </div>
    </>
  );

  // 代理購入チェックアウト結果専用ヘッダー（2ステップ・ステップ2アクティブ）
  const ProxyCheckoutResultHeader = () => (
    <>
      <div className="checkout-steps">
        <div className="step completed">
          <div className="step-number">1</div>
          <div className="step-name">{t('layout.buyer.proxy_checkout_steps.checkout')}</div>
          <div className="step-line"></div>
        </div>
        <div className="step active">
          <div className="step-number">2</div>
          <div className="step-name">{t('layout.buyer.proxy_checkout_steps.confirmation')}</div>
        </div>
      </div>
      <div className="nav-links">
        <Link to="/help" className="help-link">
          <i className="material-icons">help_outline</i>
          <span>{t('layout.buyer.header.help')}</span>
        </Link>
        <LanguageSelector />
      </div>
    </>
  );

  return (
    <>
      {/* サイドメニューオーバーレイ */}
      {sideMenuOpen && (
        <div 
          className={`side-menu-overlay ${sideMenuOpen ? 'open' : ''}`}
          onClick={closeSideMenu}
        />
      )}

      {/* オーバーレイ（サイドメニュー表示時） - 認証後かつチェックアウトページ以外で表示 */}
      {isAuthenticated && !isCheckoutPage && !isProxyCheckoutPage && !isProxyCheckoutResultPage && showSideNav && (
        <div
          className={`side-menu-overlay ${isMyPage ? 'mypage' : ''} ${showSideNav ? 'open' : ''}`}
          onClick={() => setShowSideNav(false)}
        />
      )}

      {/* モックバナー */}
      <MockBanner />

      {/* ヘッダー - カート・チェックアウトページでは専用ヘッダーを表示 */}
      <header className="header">
        <div className="header-container">
          <div className="menu-toggle" id="menu-toggle" onClick={toggleSideMenu} style={{ cursor: 'pointer' }}>
            <i className="material-icons">menu</i>
          </div>
          <Link to="/" className="logo">{t('layout.buyer.header.logo')}</Link>

          {isCartPage ? (
            <CartHeader />
          ) : isCheckoutPage ? (
            <CheckoutHeader />
          ) : isCheckoutResultPage ? (
            <CheckoutResultHeader />
          ) : isProxyCheckoutPage ? (
            <ProxyCheckoutHeader />
          ) : isProxyCheckoutResultPage ? (
            <ProxyCheckoutResultHeader />
          ) : (
            <>
              <div className="search-bar">
                <i className="material-icons" onClick={handleSearch} style={{ cursor: 'pointer' }}>search</i>
                <input
                  type="text"
                  placeholder={t('layout.buyer.header.search_placeholder')}
                  id="search-input"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <div className="nav-links">
                {isAuthenticated ? (
                  <>
                    <Link to="/cart" className="nav-link">
                      <i className="material-icons">shopping_cart</i>
                      <span>{t('layout.buyer.header.cart')}</span>
                    </Link>
                    <Link to="#" className="nav-link" id="account-toggle" onClick={(e) => { e.preventDefault(); setShowSideNav(!showSideNav); }}>
                      <i className="material-icons">account_circle</i>
                      <span>{t('layout.buyer.header.account')}</span>
                    </Link>
                  </>
                ) : (
                  <a href="#" className="nav-link" onClick={handleHeaderLoginClick}>
                    <i className="material-icons">login</i>
                    <span>{t('layout.buyer.header.login')}</span>
                  </a>
                )}
                <LanguageSelector />
              </div>
            </>
          )}
        </div>
      </header>

      {/* サイドメニュー */}
      <aside className={`side-menu ${sideMenuOpen ? 'open' : ''}`}>
        {/* 代理購入セクション - いずれかのサービスが有効な場合のみ表示 */}
        {isAnyProxyEnabled() && (
          <div className="side-section">
            <h3 className="side-section-title">
              {t('layout.buyer.side_menu.proxy.title')}
            </h3>
            <ul className="side-menu-list">
              {isYauctionEnabled() && (
                <li className="side-menu-item">
                  <Link to="/yauction/products" className="side-menu-link" onClick={closeSideMenu}>
                    <i className="material-icons">gavel</i>
                    <span>{t('layout.buyer.side_menu.proxy.yauction')}</span>
                  </Link>
                </li>
              )}
              {isMercariEnabled() && (
                <li className="side-menu-item">
                  <Link to="/mercari/products" className="side-menu-link" onClick={closeSideMenu}>
                    <i className="material-icons">shopping_bag</i>
                    <span>{t('layout.buyer.side_menu.proxy.mercari')}</span>
                  </Link>
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="side-section">
          <h3 className="side-section-title">{t('layout.buyer.side_menu.categories.title')}</h3>
          <ul className="side-menu-list">
            {categoriesLoading ? (
              <li className="side-menu-item" style={{ textAlign: 'center', padding: '1rem' }}>
                <span>Loading...</span>
              </li>
            ) : (
              parentCategories.map((parent) => (
                <React.Fragment key={parent.id}>
                  {/* 親カテゴリー */}
                  <li className="side-menu-item parent-category">
                    <Link to={`/products?category=${parent.id}`} className="side-menu-link" onClick={closeSideMenu}>
                      <i className="material-icons">{parent.icon || 'category'}</i>
                      <span>{getLocalizedText(parent.name)}</span>
                    </Link>
                  </li>
                  {/* サブカテゴリー */}
                  {subCategoriesMap[parent.id]?.map((sub) => (
                    <li key={sub.id} className="side-menu-item sub-category">
                      <Link to={`/products?category=${sub.id}`} className="side-menu-link" onClick={closeSideMenu}>
                        <i className="material-icons">{sub.icon || 'subdirectory_arrow_right'}</i>
                        <span>{getLocalizedText(sub.name)}</span>
                      </Link>
                    </li>
                  ))}
                </React.Fragment>
              ))
            )}
          </ul>
        </div>

        <div className="side-section">
          <h3 className="side-section-title">{t('layout.buyer.side_menu.account.title')}</h3>
          <ul className="side-menu-list">
            {isAuthenticated ? (
              <>
                <li className="side-menu-item">
                  <Link to="/mypage" className="side-menu-link" onClick={closeSideMenu}>
                    <i className="material-icons">account_circle</i>
                    <span>{t('layout.buyer.side_menu.account.mypage')}</span>
                  </Link>
                </li>
                <li className="side-menu-item">
                  <Link to="/mypage/orders" className="side-menu-link" onClick={closeSideMenu}>
                    <i className="material-icons">receipt</i>
                    <span>{t('layout.buyer.side_menu.account.orders')}</span>
                  </Link>
                </li>
                <li className="side-menu-item">
                  <Link to="/mypage/form-request" className="side-menu-link" onClick={closeSideMenu}>
                    <i className="material-icons">add_shopping_cart</i>
                    <span>{t('layout.buyer.side_nav.form_request')}</span>
                  </Link>
                </li>
                <li className="side-menu-item">
                  <Link to="/mypage/wishlist" className="side-menu-link" onClick={closeSideMenu}>
                    <i className="material-icons">favorite</i>
                    <span>{t('layout.buyer.side_menu.account.favorites')}</span>
                  </Link>
                </li>
              </>
            ) : (
              <li className="side-menu-item">
                <a href="#" className="side-menu-link" onClick={handleSideMenuLoginClick}>
                  <i className="material-icons">login</i>
                  <span>{t('layout.buyer.header.login')}</span>
                </a>
              </li>
            )}
          </ul>
        </div>
      </aside>

      {/* メインコンテンツ */}
      <main className={`main-content ${isMyPage ? 'mypage' : ''}`}>
        <Outlet />
        {/* サブナビゲーション（右側・トグル表示）
            認証後かつカート・チェックアウトページ以外で表示
            マイページ時は900px以上で常時表示、900px以下はトグル動作 */}
        {isAuthenticated && !isCartPage && !isCheckoutPage && !isProxyCheckoutPage && !isProxyCheckoutResultPage && (
          <aside className={`side-nav ${isMyPage ? 'mypage' : ''} ${showSideNav ? 'active' : ''}`}>
            <div className="user-profile">
              <div className="user-avatar">
                {user?.firstName?.[0]?.toUpperCase() || ''}{user?.lastName?.[0]?.toUpperCase() || ''}
              </div>
              <div className="user-name">{user?.lastName} {user?.firstName}</div>
              <div className="user-email">
                <span>{user?.email}</span>
              </div>
              {/* <div className="user-status">
                <i className="material-icons">verified</i>
                <span>{t('layout.buyer.user_profile.premium_member')}</span>
              </div> */}
            </div>

            <ul className="nav-menu" id="nav-menu">
              <li className="nav-menu-item">
                <Link to="/mypage" className="nav-menu-link">
                  <i className="material-icons">dashboard</i>
                  <span>{t('layout.buyer.side_nav.dashboard')}</span>
                </Link>
              </li>
              <li className="nav-menu-item">
                <Link to="/mypage/notifications" className="nav-menu-link">
                  <i className="material-icons">notifications</i>
                  <span>{t('layout.buyer.side_nav.notifications')}</span>
                </Link>
              </li>
              {isAnyProxyEnabled() && (
                <li className="nav-menu-item">
                  <Link to="/mypage/proxy-orders" className="nav-menu-link">
                    <i className="material-icons">sync</i>
                    <span>{t('layout.buyer.side_nav.proxy_orders')}</span>
                    {/* {dashboardStats?.proxyOrderCount !== undefined && dashboardStats.proxyOrderCount > 0 && (
                      <span className="nav-menu-badge">{dashboardStats.proxyOrderCount}</span>
                    )} */}
                  </Link>
                </li>
              )}
              <li className="nav-menu-item">
                <Link to="/mypage/orders" className="nav-menu-link">
                  <i className="material-icons">shopping_bag</i>
                  <span>{t('layout.buyer.side_nav.orders')}</span>
                </Link>
              </li>
              <li className="nav-menu-item">
                <Link to="/mypage/form-request" className="nav-menu-link">
                  <i className="material-icons">add_shopping_cart</i>
                  <span>{t('layout.buyer.side_nav.form_request')}</span>
                </Link>
              </li>
              {/* <li className="nav-menu-item">
                <Link to="/mypage/shipping" className="nav-menu-link">
                  <i className="material-icons">local_shipping</i>
                  <span>{t('layout.buyer.side_nav.shippings')}</span>
                  {dashboardStats?.shippingCount !== undefined && dashboardStats.shippingCount > 0 && (
                    <span className="nav-menu-badge">{dashboardStats.shippingCount}</span>
                  )}
                </Link>
              </li> */}
              <li className="nav-menu-item">
                <Link to="/mypage/wishlist" className="nav-menu-link">
                  <i className="material-icons">favorite</i>
                  <span>{t('layout.buyer.side_nav.favorites')}</span>
                  {/* {dashboardStats?.wishlistCount !== undefined && dashboardStats.wishlistCount > 0 && (
                    <span className="nav-menu-badge">{dashboardStats.wishlistCount}</span>
                  )} */}
                </Link>
              </li>
              {isAnyProxyEnabled() && import.meta.env.MODE !== 'production' && (
                <li className="nav-menu-item">
                  <Link to="/mypage/proxy-recommend" className="nav-menu-link">
                    <i className="material-icons">storefront</i>
                    <span>{t('layout.buyer.side_nav.proxy_recommends')}</span>
                    {/* {dashboardStats?.proxyRecommendCount !== undefined && dashboardStats.proxyRecommendCount > 0 && (
                      <span className="nav-menu-badge">{dashboardStats.proxyRecommendCount}</span>
                    )} */}
                  </Link>
                </li>
              )}
              <li className="nav-menu-item">
                <Link to="/mypage/coupons" className="nav-menu-link">
                  <i className="material-icons">card_giftcard</i>
                  <span>{t('layout.buyer.side_nav.coupons')}</span>
                  {/* {dashboardStats?.couponCount !== undefined && dashboardStats.couponCount > 0 && (
                    <span className="nav-menu-badge">{dashboardStats.couponCount}</span>
                  )} */}
                </Link>
              </li>
              <li className="nav-menu-item">
                <Link to="/mypage/chat" className="nav-menu-link">
                  <i className="material-icons">support_agent</i>
                  <span>{t('layout.buyer.side_nav.chat')}</span>
                  {/* {dashboardStats?.unreadChatCount !== undefined && dashboardStats.unreadChatCount > 0 && (
                    <span className="nav-menu-badge">{dashboardStats.unreadChatCount}</span>
                  )} */}
                </Link>
              </li>
              <li className="nav-menu-item">
                <Link to="/mypage/profile" className="nav-menu-link">
                  <i className="material-icons">person</i>
                  <span>{t('layout.buyer.side_nav.profile')}</span>
                </Link>
              </li>
              <li className="nav-menu-item">
                <Link to="/mypage/settings" className="nav-menu-link">
                  <i className="material-icons">settings</i>
                  <span>{t('layout.buyer.side_nav.settings')}</span>
                </Link>
              </li>
              <li className="nav-menu-item separator">
                <Link to="#" className="nav-menu-link" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
                  <i className="material-icons">logout</i>
                  <span>{t('layout.buyer.side_nav.logout')}</span>
                </Link>
              </li>
            </ul>
          </aside>
        )}
      </main>

      {/* フッター - カート・チェックアウトページでは簡易フッター */}
      {(isCheckoutPage || isProxyCheckoutPage) ? (
        <footer>
          <div className="footer-container">
            <div className="footer-links">
              <Link to="/terms" className="footer-link">{t('layout.buyer.footer.legal.terms')}</Link>
              <Link to="/privacy" className="footer-link">{t('layout.buyer.footer.legal.privacy')}</Link>
              <Link to="/tokutei" className="footer-link">{t('layout.buyer.footer.legal.commerce_law')}</Link>
              <Link to="/help" className="footer-link">{t('layout.buyer.footer.help.faq')}</Link>
              <Link to="/company" className="footer-link">{t('layout.buyer.footer.legal.company')}</Link>
            </div>

            <div className="copyright">
              {t('common.brand.copyright')}
            </div>
          </div>
        </footer>
      ) : (
        <footer>
          <div className="footer-container">
            <div className="footer-content">
              <div className="footer-column">
                <h3>{t('layout.buyer.footer.about.title')}</h3>
                <Link to="/company" className="footer-link">{t('layout.buyer.footer.about.company_info')}</Link>
                <Link to="/news" className="footer-link">{t('layout.buyer.footer.about.news')}</Link>
                <Link to="/careers" className="footer-link">{t('layout.buyer.footer.about.careers')}</Link>
                <Link to="/press" className="footer-link">{t('layout.buyer.footer.about.press')}</Link>
              </div>
              <div className="footer-column">
                <h3>{t('layout.buyer.footer.help.title')}</h3>
                <Link to="/user-guide" className="footer-link">{t('layout.buyer.footer.help.user_guide')}</Link>
                <Link to="/shipping" className="footer-link">{t('layout.buyer.footer.help.shipping')}</Link>
                <Link to="/returns" className="footer-link">{t('layout.buyer.footer.help.returns')}</Link>
                <Link to="/payment" className="footer-link">{t('layout.buyer.footer.help.payment')}</Link>
              </div>
              <div className="footer-column">
                <h3>{t('layout.buyer.footer.service.title')}</h3>
                <Link to="/help" className="footer-link">{t('layout.buyer.footer.help.faq')}</Link>
                <Link to="/mypage/chat" className="footer-link">{t('layout.buyer.footer.service.chat')}</Link>
                <Link to="/tokutei" className="footer-link">{t('layout.buyer.footer.legal.commerce_law')}</Link>
              </div>
              {/* <div className="footer-column">
                <h3>{t('layout.buyer.footer.social.title')}</h3>
                <div className="social-links">
                  <Link to="#" className="social-icon"><i className="fab fa-facebook-f"></i></Link>
                  <Link to="#" className="social-icon"><i className="fab fa-twitter"></i></Link>
                  <Link to="#" className="social-icon"><i className="fab fa-instagram"></i></Link>
                  <Link to="#" className="social-icon"><i className="fab fa-line"></i></Link>
                </div>
              </div> */}
            </div>

            <div className="copyright">
              {t('common.brand.copyright')}
            </div>
          </div>
        </footer>
      )}
    </>
  );
};

export default BuyerLayout;
